import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { animationFrameScheduler, fromEvent, Observable, of, Subject } from 'rxjs';
import { pairwise, startWith, switchMap, tap, throttleTime } from 'rxjs/operators';
import { ResizeObserver } from 'resize-observer';

import { GasInputBoolean } from '@cikrf/gas-utils/decorators';
import { ResizeObserverEntry } from 'resize-observer/lib/ResizeObserverEntry';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

/** @description 15px - ширина скролла (нельзя вычислить в режиме появления при прокрутке) */
const OS_SCROLL_WIDTH = 15;

/** @description 1px - добавочная ширина, во избежание вылезания нативного скрола при ресайзе */
const ASSURANCE_WIDTH = 1;

/**
 * @description Scroll component
 * Used to replace browser scroll by custom
 * @example <gas-scroll>Content</gas-scroll>
 */
@UntilDestroy()
@Component({
  selector: 'gas-scroll',
  templateUrl: './gas-scroll.component.html',
  styleUrls: ['./gas-scroll.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class._small]': `sizeType === 'small'`,
    '[class._medium]': `sizeType === 'medium'`,
    '[class._big]': `sizeType === 'big'`,
  },
})
export class GasScrollComponent implements AfterViewInit, OnChanges, OnDestroy {
  /** @description  Sets overflow-x to hidden */
  @HostBinding('class._overflow-x-hidden')
  @GasInputBoolean()
  @Input()
  public overflowXHidden: string | boolean = false;

  /** @description Sets overflow-y to hidden */
  @HostBinding('class._overflow-y-hidden')
  @GasInputBoolean()
  @Input()
  public overflowYHidden: string | boolean = false;

  @Input()
  public hideScrollbars = false;

  @Input()
  public sizeType: 'small' | 'medium' | 'big' = 'small';

  @Output('onXScroll')
  public onXScroll$ = new EventEmitter<number>();

  @Output('onYScroll')
  public onYScroll$ = new EventEmitter<number>();

  @ViewChild('container', { static: true })
  private containerRef: ElementRef;

  @ViewChild('contentWrapper', { static: true })
  private contentWrapperRef: ElementRef;

  public hasXScrollbar = false;
  public xScrolledRatio: number;
  public xViewRatio = 0;

  public hasYScrollbar = false;
  public yScrolledRatio: number;
  public yViewRatio = 0;

  private resizeObserver: ResizeObserver;

  private get container(): HTMLElement {
    return this.containerRef.nativeElement;
  }

  private get contentWrapper(): HTMLElement {
    return this.contentWrapperRef.nativeElement;
  }

  private get xHiddenLength(): number {
    return this.container.scrollWidth - this.container.clientWidth;
  }

  private get yHiddenLength(): number {
    return this.container.scrollHeight - this.container.clientHeight;
  }

  private viewChanges$ = new Subject<ResizeObserverEntry>();

  constructor(
    public elementRef: ElementRef,
    private renderer: Renderer2,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
  }

  public ngAfterViewInit(): void {
    this.viewChanges$.pipe(
      startWith({}),
      pairwise(),
      tap(() => this.setScrollExistence()),
      tap(([previous, current]: [ResizeObserverEntry, ResizeObserverEntry]) => this.hideNativeScroll(previous, current)),
      switchMap(() => this.hasXScrollbar || this.hasYScrollbar
        ? this.fromScrollEvent().pipe(tap(() => this.setScrollPosition()))
        : of(null)),
      untilDestroyed(this),
    ).subscribe(() => this.changeDetectorRef.detectChanges());

    this.resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => this.viewChanges$.next(entries[0]));

    this.resizeObserver.observe(this.contentWrapper);
    this.resizeObserver.observe(this.container);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.overflowXHidden || changes.overflowYHidden) {
      this.viewChanges$.next();
    }
  }

  public xScroll(ratio: number): void {
    this.renderer.setProperty(this.container, 'scrollLeft', Math.round(ratio * this.xHiddenLength));
    this.emitScrollLeft();
  }

  public yScroll(ratio: number): void {
    this.renderer.setProperty(this.container, 'scrollTop', Math.round(ratio * this.yHiddenLength));
    this.emitScrollTop();
  }

  public ngOnDestroy(): void {
    this.resizeObserver.unobserve(this.contentWrapper);
    this.resizeObserver.unobserve(this.container);
    this.resizeObserver.disconnect();
  }

  private fromScrollEvent(): Observable<Event> {
    return fromEvent(this.container, 'scroll', { passive: true })
      .pipe(
        throttleTime(10, animationFrameScheduler),
        startWith(null as Event),
      );
  }

  private setScrollExistence(): void {
    this.hasXScrollbar = !this.overflowXHidden && this.xHiddenLength > 0;
    this.hasYScrollbar = !this.overflowYHidden && this.yHiddenLength > 0;
  }

  private setScrollPosition(): void {
    if (this.hasXScrollbar) {
      this.xScrolledRatio = this.container.scrollLeft / this.xHiddenLength;
      this.xViewRatio = this.container.clientWidth / this.container.scrollWidth;
      this.emitScrollLeft();
    }

    if (this.hasYScrollbar) {
      this.yScrolledRatio = this.container.scrollTop / this.yHiddenLength;
      this.yViewRatio = this.container.clientHeight / this.container.scrollHeight;
      this.emitScrollTop();
    }
  }

  private hideNativeScroll(previous: ResizeObserverEntry, current: ResizeObserverEntry): void {
    const staticVerticalScrollWidth: number = Math.round(this.container.offsetWidth - this.container.clientWidth);
    const staticHorizontalScrollWidth: number = Math.round(this.container.offsetHeight - this.container.clientHeight);
    const staticScrollWidth: number = staticVerticalScrollWidth || staticHorizontalScrollWidth;
    const scrollWidth: number = Math.round(( staticScrollWidth || OS_SCROLL_WIDTH ) + ASSURANCE_WIDTH);

    if (staticHorizontalScrollWidth) {
      if (!previous?.contentRect?.height || previous.contentRect.height + 1 > current.contentRect.height) {
        this.setContainerStyle({
          marginBottom: `-${scrollWidth}px`,
          paddingBottom: `${this.getScrollbarWidth()}px`,
        });
      }
    } else {
      if (this.hasXScrollbar) {
        this.setContainerStyle({ marginBottom: `-${scrollWidth}px`, paddingBottom: `${scrollWidth}px` });
      } else {
        this.setContainerStyle({ marginBottom: null, paddingBottom: null });
      }
    }

    if (staticVerticalScrollWidth) {
      if (!previous?.contentRect?.width || previous.contentRect.width + 1 > current.contentRect.width) {
        this.setContainerStyle({
          marginRight: `-${scrollWidth}px`,
          paddingRight: `${this.getScrollbarWidth()}px`,
        });
      }
    } else {
      if (this.hasYScrollbar) {
        this.setContainerStyle({ marginRight: `-${scrollWidth}px`, paddingRight: `${scrollWidth}px` });
      } else {
        this.setContainerStyle({ marginRight: null, paddingRight: null });
      }
    }
  }

  private setContainerStyle(styles: Record<string, string>): void {
    Object.keys(styles).forEach((key: string) =>
      this.renderer.setStyle(this.container, key, styles[key]),
    );
  }

  private getScrollbarWidth(): number {
    switch (this.sizeType) {
      case 'small':
        return 4;
      case 'big':
        return 12;
      default:
        return 8;
    }
  }

  private emitScrollLeft(): void {
    this.onXScroll$.emit(this.container.scrollLeft);
  }

  private emitScrollTop(): void {
    this.onYScroll$.emit(this.container.scrollTop);
  }
}
